import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Heart, Clock, Sparkles, Shield, ArrowRight, Gift, Star, PenLine, MapPin, Phone, Mail, X, UserCircle2, Globe } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import PublicHero from "@/components/public/PublicHero";
import PublicServices from "@/components/public/PublicServices";

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={13} className={i <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-200'} />
      ))}
    </div>
  );
}

const NAV_LINKS = [
  { label: 'About', id: 'about' },
  { label: 'How to Book', id: 'how-to-book' },
  { label: 'Services', id: 'services' },
  { label: 'Team', id: 'team' },
  { label: 'Socials', id: 'socials' },
  { label: 'Reviews', id: 'reviews' },
  { label: 'Find Us', id: 'find-us' },
];

export default function PublicHome() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewsMeta, setReviewsMeta] = useState({ rating: null, total_ratings: 0 });
  const [bookdReviews, setBookdReviews] = useState([]);
  const [bookdSummary, setBookdSummary] = useState({ average_rating: 0, total: 0, breakdown: {} });
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [activeSection, setActiveSection] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [svcRes, setRes, revRes, staffRes, bookdRes, bookdSumRes] = await Promise.all([
          axios.get(`${API}/services?active_only=true`),
          axios.get(`${API}/settings`),
          axios.get(`${API}/reviews`),
          axios.get(`${API}/staff?active_only=true`),
          axios.get(`${API}/bookd-reviews?limit=6`),
          axios.get(`${API}/bookd-reviews/summary`),
        ]);
        setServices(svcRes.data);
        setSettings(setRes.data);
        setReviews(revRes.data.reviews || []);
        setReviewsMeta({ rating: revRes.data.rating, total_ratings: revRes.data.total_ratings });
        setBookdReviews(bookdRes.data?.data || []);
        setBookdSummary(bookdSumRes.data || { average_rating: 0, total: 0, breakdown: {} });
        // Only show staff who are active AND marked to show on public
        setTeamMembers((staffRes.data || []).filter(s => s.show_on_public !== false));
      } catch (e) {  }
      finally {
        setLoaded(true);
      }
    };
    load();
  }, []);

  // Highlight active nav item on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-40% 0px -55% 0px' }
    );
    NAV_LINKS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const salonSlug = settings.salon_slug || '';
  const salonName = (settings.salon_name || '').trim();

  // Website content — all editable from Settings → Website tab, with neutral fallbacks
  const heroTag = settings.hero_tag || (salonName ? `Welcome to ${salonName}` : 'Welcome');
  const heroHeadline = settings.hero_headline || salonName || 'Book your appointment';
  const heroLocation = settings.hero_headline_location || '';
  const heroDesc = settings.hero_description ||
    (salonName
      ? `Book your appointment at ${salonName} online — instant confirmation.`
      : 'Book your appointment online — instant confirmation.');
  const promoEnabled = settings.new_client_promo_enabled !== false;
  const promoAmount = settings.new_client_promo_amount ?? 5;
  const promoText = settings.new_client_promo_text || `£${promoAmount} off for new clients!`;
  const promoSub = settings.new_client_promo_sub || 'Discount applied automatically at checkout';

  const htbTitle = settings.howtobook_title || 'Book online';
  const htbSubtitle = settings.howtobook_subtitle || 'Simple, fast online booking.';
  const htbSteps = [
    { title: settings.howtobook_step1_title || 'Choose your treatment', desc: settings.howtobook_step1_desc || 'Browse our services. Not sure? Beyond AI can help.' },
    { title: settings.howtobook_step2_title || 'Pick a time', desc: settings.howtobook_step2_desc || 'See real-time availability and choose a time that works.' },
    { title: settings.howtobook_step3_title || "You're booked!", desc: settings.howtobook_step3_desc || 'Instant confirmation with a calendar invite. Cancel anytime.' },
  ];

  const servicesSectionTitle = settings.services_section_title || 'Our services';
  const servicesSectionSubtitle = settings.services_section_subtitle || 'We offer a full range of professional treatments.';

  const aboutTitle = settings.about_title || `Your local ${salonName || 'beauty salon'}`;
  const aboutPara1 = settings.about_para1 || '';
  const aboutPara2 = settings.about_para2 || '';

  const accreditations = settings.accreditations || [];

  // Show a clean tenant-neutral splash until settings have loaded — prevents a
  // flash of "Nail Salon in Staines" (or any other tenant's default copy) when
  // viewing another salon. The logo/colors come from settings as soon as ready.
  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="public-home-loading">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl mx-auto mb-3 bg-primary/10 flex items-center justify-center text-primary font-bold animate-pulse">
            B
          </div>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" style={{ scrollBehavior: 'smooth' }}>

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Top row: logo + Book Now */}
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-3">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.salon_name || 'Salon'}
                  className="w-9 h-9 rounded-xl object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-primary/10" />
              )}
              <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
                {settings.salon_name || ''}
              </span>
            </Link>
            <Link to="/book">
              <Button data-testid="public-home-book-now-button" className="rounded-xl" size="sm">
                Book Now
              </Button>
            </Link>
          </div>
          {/* Nav row */}
          <nav className="flex items-center gap-0 overflow-x-auto no-scrollbar border-t border-border/50 -mx-4 px-4 sm:mx-0 sm:px-0">
            {NAV_LINKS.map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`shrink-0 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 ${
                  activeSection === id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <PublicHero settings={settings} salonSlug={salonSlug} />


      {/* ── How to Book ── */}
      <section id="how-to-book" className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-semibold tracking-tight text-center mb-3" style={{ fontFamily: 'Playfair Display' }}>
            {htbTitle}
          </h2>
          <p className="text-sm text-muted-foreground text-center mb-10 max-w-lg mx-auto">
            {htbSubtitle}
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {htbSteps.map((step, i) => {
              const icons = [Sparkles, Clock, Shield];
              const Icon = icons[i];
              return (
                <Card key={step.title} className="p-6 rounded-2xl border bg-card shadow-[0_10px_30px_rgba(43,18,27,0.06)] card-hover text-left">
                  <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center mb-4">
                    <Icon size={22} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <PublicServices services={services} settings={settings} salonSlug={salonSlug} />


      {/* ── Meet the Team ── */}
      {teamMembers.length > 0 && (
      <section id="team" className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Meet the Team</h2>
            <p className="text-sm text-muted-foreground mt-2">Our talented team is here to make you feel amazing</p>
          </div>

          {/* Team cards */}
          <div className="flex flex-wrap justify-center gap-8">
            {teamMembers.map(member => (
              <button
                key={member.id}
                onClick={() => setSelectedMember(member)}
                className="flex flex-col items-center gap-3 group w-32 focus:outline-none"
                data-testid={`team-member-${member.id}`}
              >
                {/* Circular photo */}
                <div className="relative">
                  <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white shadow-[0_8px_24px_rgba(43,18,27,0.14)] group-hover:shadow-[0_12px_32px_rgba(43,18,27,0.22)] transition-all duration-300 group-hover:-translate-y-1">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-secondary flex items-center justify-center">
                        <UserCircle2 size={48} className="text-primary/40" />
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>{member.name}</p>
                  <p className="text-xs text-muted-foreground leading-tight mt-0.5">{member.job_title || member.role}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Staff profile popup */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto p-0">
          {selectedMember && (
            <div>
              {/* Header */}
              <div className="flex flex-col items-center pt-8 pb-4 px-6 text-center">
                <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mb-4">
                  {selectedMember.photo_url ? (
                    <img src={selectedMember.photo_url} alt={selectedMember.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-secondary flex items-center justify-center">
                      <UserCircle2 size={40} className="text-primary/40" />
                    </div>
                  )}
                </div>
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>{selectedMember.name}</h2>
                <p className="text-sm text-muted-foreground mt-0.5">{selectedMember.job_title || selectedMember.role}</p>
              </div>

              {/* Tabs */}
              <Tabs defaultValue="about" className="px-4 pb-6">
                <TabsList className="w-full rounded-xl mb-4">
                  <TabsTrigger value="about" className="flex-1 rounded-lg text-xs">About</TabsTrigger>
                  <TabsTrigger value="services" className="flex-1 rounded-lg text-xs">
                    Services {selectedMember.service_ids?.length > 0 && `(${selectedMember.service_ids.length})`}
                  </TabsTrigger>
                  <TabsTrigger value="portfolio" className="flex-1 rounded-lg text-xs">
                    Portfolio {selectedMember.portfolio?.length > 0 && `(${selectedMember.portfolio.length})`}
                  </TabsTrigger>
                  {/* Reviews tab placeholder — for future internal reviews */}
                  {/* <TabsTrigger value="reviews" className="flex-1 rounded-lg text-xs">Reviews</TabsTrigger> */}
                </TabsList>

                {/* About */}
                <TabsContent value="about" className="space-y-4 mt-0">
                  {selectedMember.bio ? (
                    <div>
                      <p className="text-sm font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>About me</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{selectedMember.bio}</p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">Bio coming soon!</p>
                  )}
                  {selectedMember.languages?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                        <Globe size={14} className="text-primary" /> Languages
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedMember.languages.map(lang => (
                          <span key={lang} className="text-xs px-2.5 py-1 bg-secondary rounded-full">{lang}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* Services */}
                <TabsContent value="services" className="mt-0">
                  {(() => {
                    const memberServices = services.filter(s => selectedMember.service_ids?.includes(s.id));
                    if (memberServices.length === 0) return (
                      <p className="text-sm text-muted-foreground text-center py-4">Service list coming soon!</p>
                    );
                    const byCat = memberServices.reduce((acc, svc) => {
                      const cat = svc.category || 'Other';
                      if (!acc[cat]) acc[cat] = [];
                      acc[cat].push(svc);
                      return acc;
                    }, {});
                    return Object.entries(byCat).map(([cat, svcs]) => (
                      <div key={cat} className="mb-4">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">{cat}</p>
                        <div className="space-y-2">
                          {svcs.map(svc => (
                            <div key={svc.id} className="flex items-center justify-between p-3 rounded-xl border bg-muted/10">
                              <div>
                                <p className="text-sm font-medium">{svc.name}</p>
                                <p className="text-xs text-muted-foreground">{svc.duration_minutes} min</p>
                              </div>
                              <span className="text-sm font-semibold text-primary">£{svc.price?.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </TabsContent>

                {/* Portfolio */}
                <TabsContent value="portfolio" className="mt-0">
                  {(selectedMember.portfolio || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">Portfolio coming soon!</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {selectedMember.portfolio.map(photo => (
                        <div key={photo.id} className="aspect-square rounded-xl overflow-hidden">
                          <img src={photo.photo_url} alt={photo.caption || 'Portfolio'} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Book Now CTA */}
              <div className="px-4 pb-6">
                <Link to="/book" onClick={() => setSelectedMember(null)}>
                  <Button className="w-full rounded-xl h-12 text-base font-semibold">
                    Book with {selectedMember.name.split(' ')[0]} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Socials ── */}
      <section id="socials" className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Playfair Display' }}>Follow {settings.salon_name || 'us'}</h2>
            <p className="text-sm text-muted-foreground">See our latest work and get in touch on social.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            <a href={settings.social_tiktok || "#"} target="_blank" rel="noopener noreferrer" className="group">
              <Card className="p-5 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] card-hover text-center">
                <div className="w-12 h-12 rounded-xl bg-black mx-auto mb-3 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.8a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.23z"/></svg>
                </div>
                <p className="font-semibold text-xs" style={{ fontFamily: 'Playfair Display' }}>TikTok</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{(settings.social_tiktok || '').split('@').pop() ? '@' + (settings.social_tiktok || '').split('@').pop() : 'TikTok'}</p>
              </Card>
            </a>
            <a href={settings.social_instagram || "#"} target="_blank" rel="noopener noreferrer" className="group">
              <Card className="p-5 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] card-hover text-center">
                <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center group-hover:scale-105 transition-transform" style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
                </div>
                <p className="font-semibold text-xs" style={{ fontFamily: 'Playfair Display' }}>Instagram</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{(settings.social_instagram || '').split('/').filter(Boolean).pop() || 'Instagram'}</p>
              </Card>
            </a>
            <a href={settings.social_facebook || "#"} target="_blank" rel="noopener noreferrer" className="group">
              <Card className="p-5 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] card-hover text-center">
                <div className="w-12 h-12 rounded-xl bg-[#1877F2] mx-auto mb-3 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </div>
                <p className="font-semibold text-xs" style={{ fontFamily: 'Playfair Display' }}>Facebook</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{settings.salon_name || 'Facebook'}</p>
              </Card>
            </a>
            <a href={settings.social_whatsapp || "#"} target="_blank" rel="noopener noreferrer" className="group">
              <Card className="p-5 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] card-hover text-center">
                <div className="w-12 h-12 rounded-xl bg-[#25D366] mx-auto mb-3 flex items-center justify-center group-hover:scale-105 transition-transform">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
                </div>
                <p className="font-semibold text-xs" style={{ fontFamily: 'Playfair Display' }}>WhatsApp</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Message us</p>
              </Card>
            </a>
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id="about" className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Playfair Display' }}>
            {aboutTitle}
          </h2>
          {aboutPara1 && (
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            {aboutPara1}
          </p>
          )}
          {aboutPara2 && (
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            {aboutPara2}
          </p>
          )}
          {/* Accreditations — dynamic from Settings → Website tab */}
          {accreditations.length > 0 && (
          <div className="inline-flex flex-col items-center gap-3 bg-card border border-border rounded-2xl px-6 py-4 mb-6 w-full max-w-lg mx-auto">
            {accreditations.map((acc, i) => (
              <div key={acc.id || i} className="flex flex-col items-center gap-2 text-center w-full">
                {acc.logo_url && <img src={acc.logo_url} alt={acc.heading || 'Accreditation'} className="h-12 object-contain" />}
                {acc.heading && <p className="text-xs font-semibold tracking-wide" style={{ fontFamily: 'Playfair Display' }}>{acc.heading}</p>}
                {acc.title && <p className="text-sm font-medium" style={{ fontFamily: 'Playfair Display' }}>{acc.title}</p>}
                {(acc.credentials || []).filter(Boolean).length > 0 && (
                  <div className="space-y-1 text-xs text-muted-foreground">
                    {(acc.credentials || []).filter(Boolean).map((cred, ci) => (
                      <p key={ci} className="flex items-center gap-2">
                        <svg className="w-3.5 h-3.5 text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        {cred}
                      </p>
                    ))}
                  </div>
                )}
                {i < accreditations.length - 1 && <div className="w-full border-t border-border mt-2" />}
              </div>
            ))}
          </div>
          )}
          <br />
          <Link to="/book">
            <Button variant="outline" className="rounded-xl">
              Book your appointment <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ── Reviews ── conditional on tenant `review_mode` setting */}
      {(() => {
        const reviewMode = settings.review_mode || 'google';
        const showGoogle = reviewMode === 'google' || reviewMode === 'both';
        const showDirect = reviewMode === 'direct' || reviewMode === 'both';
        if (!showGoogle && !showDirect) return null;
        return (
      <section id="reviews" className="py-16 bg-card" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* B∞kd verified reviews block (direct or both) */}
          {showDirect && (
            <div className="mb-12">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 mb-3">
                  <Shield size={14} className="text-primary" />
                  <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase">Verified Website Reviews</span>
                </div>
                <div className="flex items-center justify-center gap-3 mb-2">
                  <StarRating rating={Math.round(bookdSummary.average_rating || 0)} />
                  <span className="text-3xl font-semibold" style={{ fontFamily: 'Playfair Display' }} data-testid="bookd-avg">
                    {bookdSummary.average_rating > 0 ? bookdSummary.average_rating.toFixed(1) : '—'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-4" data-testid="bookd-total">
                  Based on {bookdSummary.total} verified review{bookdSummary.total === 1 ? '' : 's'}
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Link to="/reviews">
                    <Button variant="default" size="sm" className="rounded-xl gap-2" data-testid="view-all-reviews-btn">
                      View all reviews <ArrowRight size={13} />
                    </Button>
                  </Link>
                  <Link to="/reviews">
                    <Button variant="outline" size="sm" className="rounded-xl gap-2">
                      <PenLine size={13} /> Leave a review
                    </Button>
                  </Link>
                </div>
              </div>
              {bookdReviews.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {bookdReviews.map((r) => (
                    <Card key={r.id} className="p-5 rounded-2xl border shadow-sm flex flex-col gap-3 hover:shadow-md transition-shadow bg-background">
                      <div className="flex items-center justify-between">
                        <StarRating rating={r.rating} />
                        <span className="text-[10px] text-muted-foreground">{r.review_date}</span>
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed flex-1">&ldquo;{r.review_text}&rdquo;</p>
                      <div className="flex items-center gap-2.5 pt-1 border-t border-border">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                          {r.reviewer_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <span className="text-xs font-medium">{r.reviewer_name}</span>
                        {r.team_member && (
                          <span className="ml-auto text-[10px] font-medium text-primary-foreground bg-primary rounded-full px-2 py-0.5">
                            {r.team_member}
                          </span>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Google reviews block (google or both) */}
          {showGoogle && (
            <div>
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 mb-3">
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
                  <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase">Google Reviews</span>
                </div>
                <div className="flex items-center justify-center gap-3 mb-2">
                  <StarRating rating={Math.round(reviewsMeta.rating || 5)} />
                  <span className="text-3xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                    {reviewsMeta.rating?.toFixed(1) || '5.0'}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  {reviewsMeta.total_ratings} reviews on Google
                </p>
                {settings.google_place_id && (
                  <a
                    href={`https://search.google.com/local/writereview?placeid=${encodeURIComponent(settings.google_place_id)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="rounded-xl gap-2">
                      <PenLine size={13} />
                      Write a review — it means the world to us 🙏
                    </Button>
                  </a>
                )}
              </div>

              {reviews.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {reviews.map((review) => (
                    <Card key={review.author_name || review.time} className="p-5 rounded-2xl border shadow-sm flex flex-col gap-3 hover:shadow-md transition-shadow bg-background">
                      <div className="flex items-center justify-between">
                        <StarRating rating={review.rating} />
                        <span className="text-[10px] text-muted-foreground">{review.time}</span>
                      </div>
                      <p className="text-sm text-foreground/80 leading-relaxed flex-1">
                        &ldquo;{review.text}&rdquo;
                      </p>
                      <div className="flex items-center gap-2.5 pt-1 border-t border-border">
                        {review.author_photo ? (
                          <img src={review.author_photo} alt={review.author_name} className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                            {review.author_name?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                        <span className="text-xs font-medium">{review.author_name}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-8">Reviews loading...</p>
              )}
            </div>
          )}
        </div>
      </section>
        );
      })()}

      {/* ── Find Us ── */}
      <section id="find-us" className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-primary" />
              <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase">Find Us</span>
            </div>
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Visit {settings.salon_name || 'us'}</h2>
            {settings.location_summary && (
              <p className="text-sm text-muted-foreground mt-2">{settings.location_summary}</p>
            )}
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Map */}
            {(settings.google_place_id || settings.address) && (
              <div className="rounded-2xl overflow-hidden shadow-[0_10px_30px_rgba(43,18,27,0.10)] border border-border">
                <iframe
                  title={`${settings.salon_name || 'Salon'} location`}
                  src={
                    settings.google_place_id
                      ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyCa65I4ki8Kz-9W9GS-RkDJrE250fshcHM&q=place_id:${encodeURIComponent(settings.google_place_id)}&zoom=15`
                      : `https://www.google.com/maps/embed/v1/place?key=AIzaSyCa65I4ki8Kz-9W9GS-RkDJrE250fshcHM&q=${encodeURIComponent(settings.address)}&zoom=15`
                  }
                  width="100%"
                  height="360"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}

            {/* Info */}
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Getting here</h3>
                <div className="space-y-4">
                  {settings.address && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <MapPin size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Address</p>
                        <p className="text-sm text-muted-foreground whitespace-pre-line">{settings.address}</p>
                      </div>
                    </div>
                  )}
                  {settings.phone && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Phone size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Phone</p>
                        <a href={`tel:${settings.phone.replace(/\s+/g, '')}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">{settings.phone}</a>
                      </div>
                    </div>
                  )}
                  {settings.email && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Mail size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Email</p>
                        <a href={`mailto:${settings.email}`} className="text-sm text-muted-foreground hover:text-primary transition-colors">{settings.email}</a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {Array.isArray(settings.location_landmarks) && settings.location_landmarks.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-4">
                  <p className="text-xs font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>Nearby landmarks</p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {settings.location_landmarks.map((lm, i) => (
                      <li key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0" />{lm}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Link to="/book">
                <Button className="rounded-xl w-full mt-2">
                  Book your appointment <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-secondary/30 border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {settings.logo_url ? (
                <img
                  src={settings.logo_url}
                  alt={settings.salon_name || 'Salon'}
                  className="w-8 h-8 rounded-lg object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-primary/10" />
              )}
              <span className="text-sm font-medium">{settings.salon_name || ''}</span>
            </div>
            <div className="flex items-center gap-4">
              <a href={settings.social_tiktok || "#"} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.8a8.18 8.18 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.23z"/></svg>
              </a>
              <a href={settings.social_instagram || "#"} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
              </a>
              <a href={settings.social_facebook || "#"} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href={settings.social_whatsapp || "#"} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>
              </a>
            </div>
            <p className="text-xs text-muted-foreground">Powered by B∞kd</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
